import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { productService } from '../../api/productService';
import { categoryService } from '../../api/categoryService';
import { uploadService } from '../../api/uploadService';
import { MEDIA_BASE_URL } from '../../api/axios';
import './ProductForm.css';
import { brandService } from '../../api/brandService';

/* ── helpers ── */
// Returns true if the hex color is "light" (use dark text on it)
function isLightColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}

/* ── Color pill input ── */
function ColorPillInput({ pills, onChange }) {
  const [picked, setPicked] = useState('#3182ce');
  const colorInputRef = useRef(null);

  const addColor = () => {
    if (pills.includes(picked)) return;
    onChange([...pills, picked]);
  };

  return (
    <div className="color-pill-input">
      <div className="color-pills-list">
        {pills.map((hex, i) => (
          <span
            key={i}
            className="color-pill"
            style={{
              background: hex,
              color: isLightColor(hex) ? '#1a202c' : '#ffffff',
              borderColor: isLightColor(hex) ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.3)',
            }}
          >
            <span className="color-pill-swatch" style={{ background: hex }} />
            {hex.toUpperCase()}
            <button
              type="button"
              className="pill-remove"
              style={{ color: isLightColor(hex) ? '#1a202c' : '#ffffff', opacity: 0.7 }}
              onClick={() => onChange(pills.filter((_, idx) => idx !== i))}
            >
              &times;
            </button>
          </span>
        ))}
        {pills.length === 0 && (
          <span style={{ fontSize: '13px', color: '#a0aec0', fontStyle: 'italic' }}>
            Aucune couleur sélectionnée
          </span>
        )}
      </div>
      <div className="color-picker-row">
        <div
          className="color-swatch-preview"
          style={{ background: picked }}
          onClick={() => colorInputRef.current?.click()}
          title="Cliquer pour choisir une couleur"
        />
        <input
          ref={colorInputRef}
          type="color"
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
          className="native-color-input"
        />
        <span className="color-hex-label">{picked.toUpperCase()}</span>
        <button
          type="button"
          className="btn-add-color"
          onClick={addColor}
          disabled={pills.includes(picked)}
        >
          {pills.includes(picked) ? 'Déjà ajoutée' : '+ Ajouter'}
        </button>
      </div>
    </div>
  );
}

/* ── Pill input ── */
function PillInput({ pills, onChange, placeholder }) {
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef(null);

  const addPill = useCallback((raw) => {
    const value = raw.trim();
    if (!value || pills.includes(value)) return;
    onChange([...pills, value]);
    setInputVal('');
  }, [pills, onChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addPill(inputVal);
    } else if (e.key === 'Backspace' && inputVal === '' && pills.length > 0) {
      onChange(pills.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputVal.trim()) addPill(inputVal);
  };

  return (
    <div className="pill-input-wrapper" onClick={() => inputRef.current?.focus()}>
      {pills.map((pill, i) => (
        <span key={i} className="pill">
          {pill}
          <button
            type="button"
            className="pill-remove"
            onClick={(e) => { e.stopPropagation(); onChange(pills.filter((_, idx) => idx !== i)); }}
          >
            &times;
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="pill-text-input"
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={pills.length === 0 ? placeholder : ''}
      />
    </div>
  );
}

function ProductForm({ product, onSuccess, onCancel }) {
  const quillRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    sku: '',
    barcode: '',
    category_id: '',
    brand_id: '',
    main_image: '',
    html_description: '',
    price: '',
    fake_original_price: '',
    warranty_duration_months: 0,
    youtube_link: '',
    is_best_seller: false,
    is_best_price: false,
    is_active: true,
  });
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [media, setMedia] = useState([]);
  const [compatibleProducts, setCompatibleProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingEditorImage, setUploadingEditorImage] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [compatibilitySearch, setCompatibilitySearch] = useState('');
  const [productsPage, setProductsPage] = useState(1);
  const [productsPerPage] = useState(20);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  useEffect(() => {
    if (activeTab === 'compatibility') {
      fetchAllProducts(1, compatibilitySearch);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'compatibility') {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }

      const timer = setTimeout(() => {
        fetchAllProducts(1, compatibilitySearch);
      }, 500);

      setSearchDebounceTimer(timer);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [compatibilitySearch]);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        slug: product.slug || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        category_id: product.category_id || '',
        brand_id: product.brand_id || '',
        main_image: product.main_image || '',
        html_description: product.html_description || '',
        price: product.price || '',
        fake_original_price: product.fake_original_price || '',
        warranty_duration_months: product.warranty_duration_months || 0,
        youtube_link: product.youtube_link || '',
        is_best_seller: product.is_best_seller || false,
        is_best_price: product.is_best_price || false,
        is_active: product.is_active !== undefined ? product.is_active : true,
      });
      setMedia(product.media || []);
      setCompatibleProducts(
        product.compatible_products?.map((p) => p.id) || []
      );
      setColors(product.colors ? product.colors.split('|').filter(Boolean) : []);
      setSizes(product.sizes  ? product.sizes.split('|').filter(Boolean)  : []);
    }
  }, [product]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getCategories({ per_page: 100 });
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };
  const fetchBrands = async () => {
    try {
      const response = await brandService.getBrands({ per_page: 100 });
      setBrands(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchAllProducts = async (page = 1, search = '') => {
    try {
      setLoadingProducts(true);
      const params = {
        page: page,
        per_page: productsPerPage,
      };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const response = await productService.getProducts(params);
      setAllProducts(response.data || []);
      setTotalProducts(response.total || 0);
      setProductsPage(page);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      html_description: content,
    }));
  };

  // Custom image handler for Quill editor
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert('Veuillez sélectionner un fichier image');
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert('La taille de l\'image doit être inférieure à 5MB');
          return;
        }

        try {
          setUploadingEditorImage(true);

          // Upload the image
          const result = await uploadService.uploadMedia(file, 'image');

          // Get Quill editor instance
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection(true);

          // Insert the uploaded image at cursor position
          quill.insertEmbed(range.index, 'image', result.url);

          // Move cursor after the image
          quill.setSelection(range.index + 1);

        } catch (err) {
          console.error('Image upload error:', err);
          alert('Échec du téléchargement de l\'image: ' + (err.response?.data?.error || err.message));
        } finally {
          setUploadingEditorImage(false);
        }
      }
    };
  };

  // Quill modules configuration with custom image handler
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
  }), []);

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  const handleSetMainImage = (mediaItem) => {
    if (mediaItem.type === 'image') {
      setFormData((prev) => ({ ...prev, main_image: mediaItem.url }));
    }
  };

  const handleFileUpload = async (file, type) => {
    setUploading(true);
    try {
      const result = await uploadService.uploadMedia(file, type);
      const newMedia = {
        type: result.type,
        url: result.url,
        is_night_sample: false,
        is_day_sample: false,
      };
      setMedia([...media, newMedia]);
    } catch (err) {
      alert('Échec du téléchargement: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      if (file.type.startsWith('image/')) {
        handleFileUpload(file, 'image');
      }
    });
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      if (file.type.startsWith('video/')) {
        handleFileUpload(file, 'video');
      }
    });
  };

  const handleRemoveMedia = async (index) => {
    const mediaItem = media[index];
    try {
      // Try to delete from server if it's our uploaded file
      if (mediaItem.url.includes('/storage/products/')) {
        await uploadService.deleteMedia(mediaItem.url);
      }
    } catch (err) {
      console.error('Error deleting media:', err);
    }
    setMedia(media.filter((_, i) => i !== index));
  };

  const handleMediaChange = (index, field, value) => {
    const newMedia = [...media];
    newMedia[index][field] = value;
    setMedia(newMedia);
  };

  const handleAddYouTubeLink = () => {
    const link = prompt('Entrez l\'URL YouTube:');
    if (link && link.trim()) {
      setMedia([
        ...media,
        { type: 'youtube', url: link.trim(), is_night_sample: false, is_day_sample: false },
      ]);
    }
  };

  const toggleCompatibleProduct = (productId) => {
    if (compatibleProducts.includes(productId)) {
      setCompatibleProducts(compatibleProducts.filter((id) => id !== productId));
    } else {
      setCompatibleProducts([...compatibleProducts, productId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        colors: colors.join('|'),
        sizes: sizes.join('|'),
        media: media.filter((m) => m.url.trim() !== ''),
        compatible_products: compatibleProducts,
      };

      if (product) {
        await productService.updateProduct(product.id, payload);
      } else {
        await productService.createProduct(payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de l\'enregistrement du produit');
    } finally {
      setLoading(false);
    }
  };

  const getMediaPreview = (item) => {

    if (item.type === 'image') {
      return <img src={ item.url} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    } else if (item.type === 'video') {
      return <video src={item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    } else {
      return <div style={{ padding: '20px', textAlign: 'center', fontSize: '24px' }}>▶️</div>;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="product-form">
      {error && <div className="error-message">{error}</div>}

      {/* Tabs */}
      <div className="form-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Informations de base
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Média ({media.length})
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'pricing' ? 'active' : ''}`}
          onClick={() => setActiveTab('pricing')}
        >
          Prix et stock
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'compatibility' ? 'active' : ''}`}
          onClick={() => setActiveTab('compatibility')}
        >
          Compatibilité
        </button>
      </div>

      <div className="form-content">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Informations du produit</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Nom du produit *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Entrez le nom du produit"
                  />
                </div>

                <div className="form-group">
                  <label>Slug URL *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                    placeholder="slug-url-produit"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>SKU *</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    required
                    placeholder="PROD-001"
                  />
                </div>

                <div className="form-group">
                  <label>Code-barres</label>
                  <input
                    type="text"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                    placeholder="1234567890123"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select name="category_id" value={formData.category_id} onChange={handleChange}>
                    <option value="">Sélectionnez une catégorie</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Marque</label>
                  <select name="brand_id" value={formData.brand_id} onChange={handleChange}>
                    <option value="">Sélectionnez une marque</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                </div>

              <div className="form-row" style={{gridTemplateColumns: '1fr'}}>
                <div className="form-group">
                  <label>Lien vidéo YouTube</label>
                  <input
                    type="text"
                    name="youtube_link"
                    value={formData.youtube_link}
                    onChange={handleChange}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description du produit (HTML)</label>
                {uploadingEditorImage && (
                  <div className="uploading-message" style={{ marginBottom: '12px' }}>
                    Téléchargement de l'image...
                  </div>
                )}
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.html_description}
                  onChange={handleDescriptionChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Entrez la description du produit avec formatage... Cliquez sur le bouton image dans la barre d'outils pour télécharger et insérer des images."
                  style={{ backgroundColor: 'white' }}
                />
                <p style={{ fontSize: '12px', color: '#718096', marginTop: '8px' }}>
                  Utilisez le bouton image dans la barre d'outils pour télécharger et insérer des images directement dans la description
                </p>
              </div>

              <div className="form-group">
                <label>Durée de garantie (Mois)</label>
                <input
                  type="number"
                  name="warranty_duration_months"
                  value={formData.warranty_duration_months}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Variantes du produit</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Couleurs</label>
                  <ColorPillInput pills={colors} onChange={setColors} />
                </div>
                <div className="form-group">
                  <label>Tailles</label>
                  <PillInput
                    pills={sizes}
                    onChange={setSizes}
                    placeholder="Tapez une taille et appuyez sur Entrée…"
                  />
                  <small>Appuyez sur Entrée ou virgule pour ajouter. Backspace pour supprimer.</small>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Options d'affichage</h3>

              <div className="checkbox-grid">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_best_seller"
                    checked={formData.is_best_seller}
                    onChange={handleChange}
                  />
                  <span>Badge meilleure vente</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_best_price"
                    checked={formData.is_best_price}
                    onChange={handleChange}
                  />
                  <span>Badge meilleur prix</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  <span>Produit actif</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Média du produit</h3>
              <p style={{ fontSize: '14px', color: '#718096', marginBottom: '16px', padding: '12px', backgroundColor: '#f7fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <strong>Note:</strong> Téléchargez au moins une image et définissez-la comme image principale du produit. L'image principale sera affichée comme photo principale du produit sur le site web.
                {!formData.main_image && <span style={{ color: '#e53e3e', fontWeight: 'bold' }}> (Image principale requise)</span>}
              </p>

              <div className="upload-buttons">
                <label className="upload-btn">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                  Télécharger des images
                </label>

                <label className="upload-btn">
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleVideoSelect}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                  Télécharger des vidéos
                </label>

                <button
                  type="button"
                  className="upload-btn"
                  onClick={handleAddYouTubeLink}
                  disabled={uploading}
                >
                  Ajouter un lien YouTube
                </button>
              </div>

              {uploading && (
                <div className="uploading-message">
                  Téléchargement... Veuillez patienter
                </div>
              )}

              <div className="media-grid">
                {media.map((item, index) => {
                  const isMainImage = item.type === 'image' && formData.main_image === item.url;
                  return (
                    <div key={index} className={`media-item ${isMainImage ? 'main-image-item' : ''}`}>
                      <div className="media-preview">
                        {getMediaPreview(item)}
                        {isMainImage && (
                          <div className="main-image-badge">Image principale</div>
                        )}
                        <button
                          type="button"
                          className="remove-media-btn"
                          onClick={() => handleRemoveMedia(index)}
                        >
                          ×
                        </button>
                      </div>
                      <div className="media-details">
                        <span className="media-type-badge">{item.type}</span>
                        {item.type === 'image' && (
                          <button
                            type="button"
                            className={`btn-set-main ${isMainImage ? 'is-main' : ''}`}
                            onClick={() => handleSetMainImage(item)}
                            disabled={isMainImage}
                          >
                            {isMainImage ? 'Image principale' : 'Définir comme principale'}
                          </button>
                        )}
                        {item.type === 'video' && (
                          <div className="video-flags">
                            <label className="checkbox-label-small">
                              <input
                                type="checkbox"
                                checked={item.is_night_sample}
                                onChange={(e) =>
                                  handleMediaChange(index, 'is_night_sample', e.target.checked)
                                }
                              />
                              Échantillon de nuit
                            </label>
                            <label className="checkbox-label-small">
                              <input
                                type="checkbox"
                                checked={item.is_day_sample}
                                onChange={(e) =>
                                  handleMediaChange(index, 'is_day_sample', e.target.checked)
                                }
                              />
                              Échantillon de jour
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {media.length === 0 && (
                <div className="empty-state">
                  <p>Aucun média ajouté</p>
                  <p className="empty-state-hint">Téléchargez des images, vidéos ou ajoutez des liens YouTube pour présenter votre produit</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pricing & Stock Tab */}
        {activeTab === 'pricing' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Tarification</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Prix de vente *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Prix d'origine (pour affichage de promo)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="fake_original_price"
                    value={formData.fake_original_price}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                  <small>Laisser vide si pas en promotion</small>
                </div>
              </div>

              {formData.fake_original_price && formData.price && (
                <div className="price-preview">
                  <span className="original-price">{parseFloat(formData.fake_original_price).toFixed(2)} DH</span>
                  <span className="sale-price">{parseFloat(formData.price).toFixed(2)} DH</span>
                  <span className="discount-badge">
                    {Math.round(
                      ((parseFloat(formData.fake_original_price) - parseFloat(formData.price)) /
                        parseFloat(formData.fake_original_price)) *
                        100
                    )}
                    % de réduction
                  </span>
                </div>
              )}
            </div>

            <div className="form-section">
              <h3>Inventaire</h3>

              <div className="form-group">
                <label>Stock actuel (Lecture seule)</label>
                <input
                  type="number"
                  value={product?.stock_quantity || 0}
                  disabled
                  style={{ background: '#f7fafc', cursor: 'not-allowed' }}
                />
                <p style={{ fontSize: '12px', color: '#718096', marginTop: '8px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                  <strong>Note:</strong> La quantité en stock est automatiquement calculée à partir de tous les mouvements de stock.
                  {!product && <span style={{ color: '#0c4a6e', fontWeight: 'bold' }}> Après avoir créé ce produit, ajoutez le stock initial via la page Mouvements de stock.</span>}
                  {product && <span style={{ color: '#0c4a6e' }}> Utilisez la page <strong>Mouvements de stock</strong> pour ajouter ou retirer du stock.</span>}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Compatibility Tab */}
        {activeTab === 'compatibility' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Produits compatibles</h3>
              <p className="section-description">
                Sélectionnez les produits compatibles avec cet article (par ex., accessoires, articles connexes)
              </p>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  placeholder="Rechercher des produits par nom, SKU ou code-barres..."
                  value={compatibilitySearch}
                  onChange={(e) => setCompatibilitySearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
                {!loadingProducts && totalProducts > 0 && (
                  <p style={{ fontSize: '12px', color: '#718096', marginTop: '8px' }}>
                    {totalProducts} produit(s) trouvé(s)
                  </p>
                )}
              </div>

              {loadingProducts && (
                <div className="uploading-message" style={{ textAlign: 'center', padding: '20px' }}>
                  Chargement des produits...
                </div>
              )}

              {!loadingProducts && (
                <>
                  <div className="products-selection-grid">
                    {allProducts
                      .filter((p) => !product || p.id !== product.id)
                      .map((p) => (
                        <label key={p.id} className="product-checkbox">
                          <input
                            type="checkbox"
                            checked={compatibleProducts.includes(p.id)}
                            onChange={() => toggleCompatibleProduct(p.id)}
                          />
                          <span>{p.name}</span>
                          <small style={{ color: '#718096', fontSize: '12px' }}>SKU: {p.sku}</small>
                        </label>
                      ))}
                  </div>

                  {allProducts.filter((p) => !product || p.id !== product.id).length === 0 && (
                    <div className="empty-state">
                      <p>{compatibilitySearch ? 'Aucun produit trouvé correspondant à votre recherche' : 'Aucun autre produit disponible'}</p>
                    </div>
                  )}

                  {totalProducts > productsPerPage && (
                    <div className="pagination-controls" style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '16px',
                      marginTop: '20px',
                      padding: '16px',
                      borderTop: '1px solid #e2e8f0'
                    }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => fetchAllProducts(productsPage - 1, compatibilitySearch)}
                        disabled={productsPage === 1 || loadingProducts}
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        ← Précédent
                      </button>
                      <span style={{ fontSize: '14px', color: '#4a5568' }}>
                        Page {productsPage} / {Math.ceil(totalProducts / productsPerPage)}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => fetchAllProducts(productsPage + 1, compatibilitySearch)}
                        disabled={productsPage >= Math.ceil(totalProducts / productsPerPage) || loadingProducts}
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        Suivant →
                      </button>
                    </div>
                  )}
                </>
              )}

              <div className="selection-summary" style={{
                marginTop: '16px',
                padding: '12px 16px',
                backgroundColor: compatibleProducts.length > 0 ? '#f0fdf4' : '#f7fafc',
                border: `1px solid ${compatibleProducts.length > 0 ? '#86efac' : '#e2e8f0'}`,
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: compatibleProducts.length > 0 ? '#166534' : '#4a5568'
              }}>
                {compatibleProducts.length > 0 ? (
                  <>
                    ✓ {compatibleProducts.length} produit(s) compatible(s) sélectionné(s)
                  </>
                ) : (
                  'Aucun produit compatible sélectionné'
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading || uploading}>
          {loading ? 'Enregistrement...' : product ? 'Mettre à jour le produit' : 'Créer le produit'}
        </button>
      </div>
    </form>
  );
}

export default ProductForm;
